import { ActivityFeed } from "@/components/ActivityFeed";
import { PluginSlot } from "@/components/plugin";
import { createSimpleFilter } from "@/components/filters/adapters/protobufAdapter";
import { PromptBox } from "@/components/PromptBox";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	GraduationScreen,
	ONBOARDING_STEPS,
	WelcomeScreen,
	useOnboarding,
} from "@/features/onboarding";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import { CommunicationService } from "@/gen/omnios/v1/communication_connect";
import { ConversationKind } from "@/gen/omnios/v1/communication_pb";
import { DecisionStatus } from "@/gen/omnios/v1/accounting_pb";
import { OrderService } from "@/gen/omnios/v1/order_connect";
import { useAgentStore } from "@/lib/agent";
import { useAuthStore } from "@/lib/auth";
import { formatRelative } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useListDecisions } from "@/hooks/use-accounting";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	CheckSquare,
	ClipboardList,
	MessageCircle,
	MessageSquare,
	Package,
	ShoppingCart,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
	component: HomePage,
});

const catalogClient = createClient(CatalogService, transport);
const commClient = createClient(CommunicationService, transport);
const orderClient = createClient(OrderService, transport);

interface PriorityCardProps {
	title: string;
	description: string;
	count?: number;
	isLoading?: boolean;
	icon: React.ComponentType<{ className?: string }>;
	color: string;
	actionLabel: string;
	onAction: () => void;
}

function formatCount(n: number): string {
	if (n >= 1_000_000)
		return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
	if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
	return n.toString();
}

function PriorityCard({
	title,
	description,
	count,
	isLoading,
	icon: Icon,
	color,
	actionLabel,
	onAction,
}: PriorityCardProps) {
	return (
		<Card className="border-muted/70">
			<CardContent className="flex h-full flex-col gap-4 p-4">
				<div className="flex items-start gap-3">
					<div className={cn("rounded-xl p-2.5", color)}>
						<Icon className="h-4 w-4" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-medium">{title}</p>
						<p className="mt-1 text-sm text-muted-foreground">{description}</p>
					</div>
				</div>
				<div className="mt-auto flex items-center justify-between gap-3">
					{isLoading ? (
						<Skeleton className="h-8 w-16" />
					) : (
						<p className="text-2xl font-semibold tabular-nums">
							{formatCount(count ?? 0)}
						</p>
					)}
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="gap-1.5"
						onClick={onAction}
					>
						{actionLabel}
						<ArrowRight className="h-3.5 w-3.5" />
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

function HomePage() {
	const { user } = useAuthStore();
	const { setInitialPrompt, setInitialEntities } = useAgentStore();
	const navigate = useNavigate();
	const { isActive, hasStarted, currentStep, mode, chatThreadId } =
		useOnboarding();
	const { t } = useTranslation("activity");
	const { t: tc } = useTranslation("common");
	const selectedWorkspace = useWorkspaceStore((s) => s.selected());
	const hasFeature = (f: string) =>
		selectedWorkspace?.features?.includes(f) ?? false;

	function getGreeting(): string {
		const hour = new Date().getHours();
		if (hour < 6) return t("greeting_night");
		if (hour < 12) return t("greeting_morning");
		if (hour < 18) return t("greeting_afternoon");
		return t("greeting_evening");
	}

	const suggestions = [
		{
			title: t("suggestion_no_photo"),
			description: t("suggestion_no_photo_desc"),
		},
		{
			title: t("suggestion_my_tasks"),
			description: t("suggestion_my_tasks_desc"),
		},
		{
			title: t("suggestion_orders_summary"),
			description: t("suggestion_orders_summary_desc"),
		},
		{
			title: t("suggestion_compose_order"),
			description: t("suggestion_compose_order_desc"),
		},
	];

	const hasCatalog = hasFeature("catalog");
	const hasCommerce = hasFeature("commerce");
	const hasAccounting = hasFeature("accounting");
	const hasAgent = hasFeature("agent");

	const draftProductsQuery = useQuery({
		queryKey: ["home", "products", "drafts"],
		queryFn: () =>
			catalogClient.listProducts({
				page: { pageSize: 1, pageToken: "" },
				filter: createSimpleFilter("status", "eq", "draft"),
				excludeVariants: true,
			}),
		staleTime: 60_000,
		enabled: hasCatalog,
	});

	const lowQualityProductsQuery = useQuery({
		queryKey: ["home", "products", "low-quality"],
		queryFn: () =>
			catalogClient.listProducts({
				page: { pageSize: 1, pageToken: "" },
				filter: createSimpleFilter("quality_score", "lt", 50),
				excludeVariants: true,
			}),
		staleTime: 60_000,
		enabled: hasCatalog,
	});

	const recentOrdersQuery = useQuery({
		queryKey: ["home", "orders", "recent"],
		queryFn: () =>
			orderClient.listOrders({ page: { pageSize: 5, pageToken: "" } }),
		staleTime: 30_000,
		enabled: hasCommerce,
	});

	const approvalsQuery = useListDecisions({
		status: DecisionStatus.PROPOSED,
	});

	const agentThreadsQuery = useQuery({
		queryKey: ["agent-threads", "recent"],
		queryFn: () =>
			commClient.listConversations({
				kind: ConversationKind.AGENT_THREAD,
				page: { pageSize: 5, pageToken: "" },
			}),
		staleTime: 30_000,
		enabled: hasAgent,
	});

	const recentThreads = useMemo(
		() =>
			(agentThreadsQuery.data?.conversations ?? []).slice().sort((a, b) => {
				const aTime = a.updatedAt?.toDate().getTime() ?? 0;
				const bTime = b.updatedAt?.toDate().getTime() ?? 0;
				return bTime - aTime;
			}),
		[agentThreadsQuery.data?.conversations],
	);

	const recentOrders = useMemo(
		() =>
			(recentOrdersQuery.data?.orders ?? []).slice().sort((a, b) => {
				const aTime =
					(a.updatedAt?.seconds ? Number(a.updatedAt.seconds) * 1000 : 0) || 0;
				const bTime =
					(b.updatedAt?.seconds ? Number(b.updatedAt.seconds) * 1000 : 0) || 0;
				return bTime - aTime;
			}),
		[recentOrdersQuery.data?.orders],
	);

	const [submitting, setSubmitting] = useState(false);

	if (isActive && currentStep >= 5) {
		return <GraduationScreen />;
	}

	if (!hasStarted) {
		return <WelcomeScreen />;
	}

	const handleSubmit = async (
		text: string,
		promptEntities: {
			entityType: string;
			entityId: string;
			displayText: string;
		}[],
	) => {
		if (!text && promptEntities.length === 0) return;
		if (submitting) return;
		setSubmitting(true);

		try {
			const conv = await commClient.createConversation({
				subject: text.slice(0, 80) || tc("new_dialog"),
				kind: ConversationKind.AGENT_THREAD,
			});

			setInitialPrompt(text);
			if (promptEntities.length > 0) {
				setInitialEntities(promptEntities);
			}

			navigate({
				to: "/assistant/$threadId",
				params: { threadId: conv.id },
			});
		} catch (e) {
			console.error("Failed to start session", e);
			toast.error(tc("start_conversation_failed"));
			setSubmitting(false);
		}
	};

	const firstName = user?.name?.split(" ")[0] ?? "";
	const approvals = approvalsQuery.data?.decisions ?? [];

	const openProductFilter = (query: Record<string, string>) => {
		navigate({ to: "/catalog/products", search: query as never });
	};

	return (
		<div className="flex flex-col items-center min-h-[calc(100vh-theme(spacing.24))]">
			<div className="w-full max-w-6xl space-y-6 px-4 py-8">
				{isActive &&
					mode === "chat" &&
					chatThreadId &&
					currentStep < ONBOARDING_STEPS.length && (
						<Link
							to="/assistant/$threadId"
							params={{ threadId: chatThreadId }}
							className="group flex items-center gap-3 rounded-2xl border border-primary/25 bg-primary/[0.04] px-4 py-3 transition-all hover:border-primary/40 hover:bg-primary/[0.06] hover:shadow-sm"
						>
							<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/15">
								<MessageCircle className="h-5 w-5 text-primary" />
							</div>
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium">
									{t("continue_onboarding")}
								</p>
								<p className="text-xs text-muted-foreground">
									{t("step_of", {
										current: currentStep + 1,
										total: ONBOARDING_STEPS.length,
									})}
									{ONBOARDING_STEPS[currentStep] && (
										<> &middot; {t(ONBOARDING_STEPS[currentStep].labelKey)}</>
									)}
								</p>
							</div>
							<ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
						</Link>
					)}

				<div className="grid gap-6 xl:grid-cols-[1.45fr_1fr]">
					<div className="space-y-6">
						<div className="space-y-2">
							<p className="text-sm font-medium text-primary">
								{t("mission_title")}
							</p>
							<h1 className="text-3xl font-semibold tracking-tight">
								{getGreeting()}
								{firstName ? `, ${firstName}` : ""}
							</h1>
							<p className="max-w-2xl text-sm text-muted-foreground">
								{t("mission_subtitle")}
							</p>
						</div>

						{hasAgent && (
							<div className="space-y-3">
								<div className="flex flex-wrap gap-2">
									{suggestions.map((suggestion) => (
										<Button
											key={suggestion.title}
											variant="outline"
											size="sm"
											onClick={() =>
												handleSubmit(suggestion.description, [])
											}
											className="gap-1.5 rounded-full px-3.5 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
										>
											{suggestion.title}
										</Button>
									))}
								</div>

								<PromptBox
									onSubmit={handleSubmit}
									disabled={submitting}
									placeholder={t("prompt_placeholder")}
									autoFocus
									allowAttachments={false}
								/>
							</div>
						)}

					</div>

					<div className="space-y-4">
						<div className="space-y-1">
							<h2 className="text-sm font-medium">{t("priority_title")}</h2>
							<p className="text-sm text-muted-foreground">
								{t("priority_subtitle")}
							</p>
						</div>
						<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
							{hasAccounting && (
								<PriorityCard
									title={t("priority_approvals_title")}
									description={t("priority_approvals_desc")}
									count={approvals.length}
									isLoading={approvalsQuery.isLoading}
									icon={ClipboardList}
									color="bg-amber-500/10 text-amber-600"
									actionLabel={t("review_now")}
									onAction={() => navigate({ to: "/accounting" })}
								/>
							)}
							{hasCatalog && (
								<PriorityCard
									title={t("priority_drafts_title")}
									description={t("priority_drafts_desc")}
									count={draftProductsQuery.data?.page?.totalCount}
									isLoading={draftProductsQuery.isLoading}
									icon={Package}
									color="bg-blue-500/10 text-blue-600"
									actionLabel={t("review_now")}
									onAction={() =>
										openProductFilter({ "f.status": "eq:draft" })
									}
								/>
							)}
							{hasCatalog && (
								<PriorityCard
									title={t("priority_quality_title")}
									description={t("priority_quality_desc")}
									count={lowQualityProductsQuery.data?.page?.totalCount}
									isLoading={lowQualityProductsQuery.isLoading}
									icon={CheckSquare}
									color="bg-rose-500/10 text-rose-600"
									actionLabel={t("review_now")}
									onAction={() =>
										openProductFilter({ "f.quality_score": "lt:50" })
									}
								/>
							)}
							{hasAgent && (
								<PriorityCard
									title={t("priority_conversations_title")}
									description={t("priority_conversations_desc")}
									count={recentThreads.length}
									isLoading={agentThreadsQuery.isLoading}
									icon={MessageSquare}
									color="bg-emerald-500/10 text-emerald-600"
									actionLabel={t("open_assistant")}
									onAction={() => navigate({ to: "/assistant" })}
								/>
							)}
						</div>
					</div>
				</div>

				<div className={cn(
					"grid gap-6",
					hasAgent && hasCommerce ? "xl:grid-cols-2" : "",
				)}>
					{hasAgent && (
						<Card className="border-muted/70">
							<CardHeader className="pb-3">
								<div className="flex items-center justify-between gap-3">
									<CardTitle className="text-sm">
										{t("continue_conversations")}
									</CardTitle>
									<Link
										to="/assistant"
										className="text-xs text-muted-foreground transition-colors hover:text-foreground"
									>
										{tc("view_all")}
									</Link>
								</div>
							</CardHeader>
							<CardContent className="space-y-1">
								{recentThreads.length === 0 ? (
									<p className="py-6 text-sm text-muted-foreground">
										{t("no_recent_conversations")}
									</p>
								) : (
									recentThreads.map((thread) => (
										<Link
											key={thread.id}
											to="/assistant/$threadId"
											params={{ threadId: thread.id }}
											className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-muted"
										>
											<MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
											<span className="min-w-0 flex-1 truncate text-sm">
												{thread.subject || tc("new_dialog")}
											</span>
											<span className="shrink-0 text-[11px] text-muted-foreground/50">
												{thread.updatedAt
													? formatRelative(thread.updatedAt.toDate())
													: ""}
											</span>
										</Link>
									))
								)}
							</CardContent>
						</Card>
					)}

					{hasCommerce && (
						<Card className="border-muted/70">
							<CardHeader className="pb-3">
								<div className="flex items-center justify-between gap-3">
									<CardTitle className="text-sm">
										{t("continue_orders")}
									</CardTitle>
									<Link
										to="/commerce/orders"
										className="text-xs text-muted-foreground transition-colors hover:text-foreground"
									>
										{tc("view_all")}
									</Link>
								</div>
							</CardHeader>
							<CardContent className="space-y-1">
								{recentOrders.length === 0 ? (
									<p className="py-6 text-sm text-muted-foreground">
										{t("no_recent_orders")}
									</p>
								) : (
									recentOrders.map((order) => (
										<Link
											key={order.id}
											to="/commerce/orders/$id"
											params={{ id: order.id }}
											className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-muted"
										>
											<ShoppingCart className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium">
													{order.title || `#${order.seqNo}`}
												</p>
												<p className="truncate text-[11px] text-muted-foreground">
													#{order.seqNo} &middot; {order.lines.length}{" "}
													{t("lines_label")}
												</p>
											</div>
											<span className="shrink-0 text-[11px] text-muted-foreground/50">
												{order.updatedAt
													? formatRelative(
															new Date(Number(order.updatedAt.seconds) * 1000),
														)
													: ""}
											</span>
										</Link>
									))
								)}
							</CardContent>
						</Card>
					)}

				</div>

				<Card className="border-muted/70">
					<CardHeader className="pb-3">
						<div className="flex items-center justify-between">
							<CardTitle className="text-sm">{t("activity_label")}</CardTitle>
							<Link
								to="/activity"
								className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
							>
								{t("activity_all")}
								<ArrowRight className="h-3.5 w-3.5" />
							</Link>
						</div>
					</CardHeader>
					<CardContent>
						<ActivityFeed limit={5} />
					</CardContent>
				</Card>
			</div>

			{/* Plugin dashboard widgets */}
			<PluginSlot
				slot="dashboard.widget"
				context={{}}
				className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6"
			/>
		</div>
	);
}
